import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Menu } from "antd";
import type { MenuProps } from "antd";
import Icon, { AlertOutlined, ClusterOutlined } from "@ant-design/icons";
import GeneralLayout from "../GeneralLayout";
import {
  LocationSvg,
  ParametersMenuSvg,
  SimulationMenuSvg,
  UploadFilesSvg,
  BlockDiagramIconSvg,
  UserViewSvg,
  NonRevenueWaterSvg,
} from "@/assets/svg";
import "./index.scss";
import KpiSvg from "../../assets/svg/KpiSvg";

const ConfigurationLayout: React.FC = () => {
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement>(null);
  const [selectedKey, setSelectedKey] = React.useState("1");
  
  const security = JSON.parse(localStorage.getItem("security") || "{}");
  const showUserManager =
    security.role === "Administrator" || security.role === "SuperAdmin";
  
  const LocationIcon = () => <Icon component={LocationSvg} />;
  const UserViewIcon = () => <Icon component={UserViewSvg} />;
  const ParametersIcon = () => <Icon component={ParametersMenuSvg} />;
  const SimulationsIcon = () => <Icon component={SimulationMenuSvg} />;
  const UploadFilesIcon = () => <Icon component={UploadFilesSvg} />;
  const BlockDiagramIcon = () => <Icon component={BlockDiagramIconSvg} />;
  const KpiIcon = () => <Icon component={KpiSvg} />;
  const NonRevenueWaterIcon = () => <Icon component={NonRevenueWaterSvg} />;
  
  type MenuItem = Required<MenuProps>["items"][number] & { key: string };

  //const security = localStorage.getItem('security');
  const userData = security ? security : null;

  // Extract modules from userData
  const modules = userData?.modules || [];

  // Determine if NRW module exists; hide legacy Non Revenue Water item
  const hasNrwModule = Array.isArray(modules) && modules.some(
    (m: any) => m?.name === 'NRW' && m?.id === 7
  );

  function getItem(
    label: React.ReactNode,
    key: string,
    icon?: React.ReactNode,
    children?: MenuItem[]
  ): MenuItem {
    return {
      key,
      icon,
      children,
      label,
    } as MenuItem;
  }

  const items: MenuItem[] = [
    getItem("Locations", "1", <LocationIcon />),
    getItem("Upload Files", "2", <UploadFilesIcon />),
    getItem("Assets", "3", <ParametersIcon />),
    getItem("KPIs", "4", <KpiIcon />),
    getItem("Block Diagram", "5", <BlockDiagramIcon />),
    getItem("Simulations", "6", <SimulationsIcon />),
    getItem("Devices", "7", <UploadFilesIcon />),
    getItem("Alerts", "8", <AlertOutlined />),
    getItem("Smart Controls", "9", <ClusterOutlined />),
    ...(hasNrwModule ? [getItem("Non Revenue Water", "10", <NonRevenueWaterIcon />)] : []),
  ];

  const itemsBottom: MenuItem[] = [
    getItem("User Manager", "50", <UserViewIcon />),
  ];

  function redirectTo(key: string) {
    setSelectedKey(key);
    switch (key) {
      case "1":
        return navigate("/locations");
      case "2":
        return navigate("/upload");
      case "3":
        return navigate("/infrastructures");
      case "4":
        return navigate("/kpi");
      case "5":
        return navigate("/block/diagram");
      case "6":
        return navigate("simulations");
      case "7":
        return navigate("/devices");
      case "8":
        return navigate("/alerts");
      case "9":
        return navigate("/stateMachines");
      case "10":
        return navigate("/nonRevenueWater");
      case "50":
        return navigate("/users");
      default:
        break;
    }
  }

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!menuRef.current) return;

      if (e.key === 'Tab') {
        e.preventDefault();
        
        const allKeys = [
          ...items.map(item => item.key), 
          ...(showUserManager ? itemsBottom.map(item => item.key) : [])
        ];
        
        const currentIndex = allKeys.indexOf(selectedKey);
        let newIndex;
        
        if (e.shiftKey) {
          // Shift + Tab - move backward
          newIndex = currentIndex <= 0 ? allKeys.length - 1 : currentIndex - 1;
        } else {
          // Tab - move forward
          newIndex = currentIndex >= allKeys.length - 1 ? 0 : currentIndex + 1;
        }
        
        redirectTo(allKeys[newIndex]);
      }
    };

    const menuElement = menuRef.current;
    menuElement?.addEventListener('keydown', handleKeyDown);

    return () => {
      menuElement?.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedKey, showUserManager]);

  return (
    <GeneralLayout>
      <div style={{ marginBottom: "1rem", color: 'black', textAlign: 'center', fontWeight: 'bold' }}>
        <h5>CONFIGURATION</h5>
      </div>
      <div ref={menuRef} tabIndex={0} style={{ outline: 'none' }}>
        <Menu
          selectedKeys={[selectedKey]}
          mode="inline"
          items={items}
          style={{ backgroundColor: "#f7f9fc", border: "none" }}
          onClick={({ key }) => redirectTo(key as string)}
        />
        {showUserManager && (
          <div style={{ marginTop: "auto" }}>
            <Menu
              selectedKeys={[selectedKey]}
              mode="inline"
              items={itemsBottom}
              style={{ backgroundColor: "#f7f9fc", border: "none" }}
              onClick={({ key }) => redirectTo(key as string)}
            />
          </div>
        )}
      </div>
    </GeneralLayout>
  );
};

export default ConfigurationLayout;